import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  useEmailMsrPurchaseOrder,
  type MsrPurchaseOrderEmail,
} from '~/api/order';
import { useSellerContacts } from '~/api/sellerContacts';
import { SelectContactsBody } from '~/components/common/SelectContactsModal';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { ClipSpinner } from '~/components/ui';
import { SectionPanel } from '~/components/ui/SectionPanel';
import { OutboundEmailStatus } from '~/graphql/graphql';
import { countsAsEmailed } from '~/pages/order-view/sections/purchaseOrderEmails';
import { formatDate, formatDateTime, toastGraphQLError } from '~/utils';
import { contactLabel, type ContactDetails } from '~/utils/contact';
import type { MsrPurchaseOrderPrintOptions } from '~/utils/msrPurchaseOrderUrl';

const STATE_STYLES: Record<OutboundEmailStatus, string> = {
  [OutboundEmailStatus.Pending]: 'text-warning-700',
  [OutboundEmailStatus.Sent]: 'text-success-700',
  [OutboundEmailStatus.Failed]: 'text-error-600',
};

const STATE_KEYS: Record<OutboundEmailStatus, string> = {
  [OutboundEmailStatus.Pending]:
    'order.email_purchase_order_modal.state_pending',
  [OutboundEmailStatus.Sent]: 'order.email_purchase_order_modal.state_sent',
  [OutboundEmailStatus.Failed]: 'order.email_purchase_order_modal.state_failed',
};

type EmailMsrPurchaseOrderModalProps = Readonly<{
  onClose: () => void;
  orderId: string;
  printOptions: MsrPurchaseOrderPrintOptions;
  purchaseOrderNumber: string;
  sellingOrganizationId: string;
  initialContactIds: string[];
  purchaseOrderEmails: MsrPurchaseOrderEmail[];
  primaryContactId: string | undefined;
}>;

export function EmailMsrPurchaseOrderModal({
  onClose,
  orderId,
  printOptions,
  purchaseOrderNumber,
  sellingOrganizationId,
  initialContactIds,
  purchaseOrderEmails,
  primaryContactId,
}: EmailMsrPurchaseOrderModalProps) {
  const { t } = useTranslation('order');
  const { contacts, status: contactsStatus } = useSellerContacts(
    sellingOrganizationId
  );
  const { mutate: emailPurchaseOrder, isPending: isSending } =
    useEmailMsrPurchaseOrder();

  const handleClose = () => {
    if (isSending) {
      return;
    }
    onClose();
  };

  const send = (contactIds: string[]) => {
    emailPurchaseOrder(
      { orderId, contactIds, printOptions },
      {
        onSuccess: () => {
          toast.success(
            t('order.email_purchase_order_modal.sent', {
              count: contactIds.length,
            })
          );
          onClose();
        },
        onError: toastGraphQLError,
      }
    );
  };

  const lastEmailedAt = lastEmailedAtByRecipient(purchaseOrderEmails);
  const emailedNote = (contact: ContactDetails) => {
    const emailedAt =
      contact.externalId && lastEmailedAt.get(contact.externalId.toLowerCase());
    return emailedAt
      ? t('order.email_purchase_order_modal.emailed_on', {
          date: formatDate(emailedAt),
        })
      : undefined;
  };

  const renderBody = () => {
    if (contactsStatus === 'loading') {
      return (
        <div className="px-6 py-4">
          <ClipSpinner />
        </div>
      );
    }
    if (contactsStatus === 'failed') {
      return (
        <p className="px-6 py-4 text-sm text-error-600">
          {t('order.email_purchase_order_modal.load_failed')}
        </p>
      );
    }
    return (
      <SelectContactsBody
        onClose={handleClose}
        organizationId={sellingOrganizationId}
        contacts={contacts}
        initialContactIds={initialContactIds}
        initialPrimaryContactId={primaryContactId}
        primaryContactMode="readOnly"
        selectionRequired
        confirmLabel={t('order.email_purchase_order_modal.send')}
        noContactsMessage={t('order.email_purchase_order_modal.no_contacts')}
        contactNote={emailedNote}
        onConfirm={(contactIds) => send(contactIds)}
        isSubmitting={isSending}
      />
    );
  };

  return (
    <Modal isOpen onClose={handleClose}>
      <div
        className="bg-neut-light rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col"
        data-testid="email-purchase-order-modal"
      >
        <ModalHeader
          title={t('order.email_purchase_order_modal.title', {
            purchaseOrderNumber,
          })}
          description={t('order.email_purchase_order_modal.subtitle')}
          onClose={handleClose}
          closeDisabled={isSending}
        />
        {purchaseOrderEmails.length > 0 && (
          <div className="px-6 pt-4 shrink-0 max-h-[30vh] overflow-y-auto">
            <EmailHistory emails={purchaseOrderEmails} />
          </div>
        )}
        {renderBody()}
      </div>
    </Modal>
  );
}

function EmailHistory({
  emails,
}: Readonly<{ emails: MsrPurchaseOrderEmail[] }>) {
  const { t } = useTranslation('order');
  return (
    <section data-testid="purchase-order-email-history">
      <SectionPanel
        title={t('order.email_purchase_order_modal.history_title')}
        bodyClassName="p-0"
      >
        <ul className="divide-y divide-neut-border">
          {emails.map((email) => (
            <li
              key={email.id}
              className="px-3 py-2 flex flex-wrap items-baseline gap-x-2 text-sm"
            >
              <span className="min-w-0 break-all">{email.recipientTo}</span>
              <span
                className={`text-xs font-medium ${STATE_STYLES[email.status]}`}
              >
                {t(STATE_KEYS[email.status])}
              </span>
              <span className="basis-full text-xs text-neut-sec">
                {email.requestedBy
                  ? t('order.email_purchase_order_modal.sent_by', {
                      date: formatDateTime(email.createdAt),
                      name: contactLabel(email.requestedBy),
                    })
                  : formatDateTime(email.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      </SectionPanel>
    </section>
  );
}

function lastEmailedAtByRecipient(emails: MsrPurchaseOrderEmail[]) {
  const lastEmailedAt = new Map<string, string>();
  for (const email of emails.filter(countsAsEmailed)) {
    const recipient = email.recipientTo.toLowerCase();
    if (!lastEmailedAt.has(recipient)) {
      lastEmailedAt.set(recipient, email.createdAt);
    }
  }
  return lastEmailedAt;
}
